import { IServerPagination } from "app/model"
import { getRepositoryDate } from "app/util/date/tools/common"
import { setAudienceDefaultParams } from "audience/tools/common"
import { CampaignAPI, ForecastAPI } from "generated-api"
import { IBoundaryBox } from "geometry/model"
import { NOT_FOUND_PATH } from "page/model"
import { checkIfSessionIsOrWillExpired } from "user/tools/common"
import {
  ALL_CAMPAIGN_STATUSES,
  ALL_FORECAST_STATUSES,
  CAMPAIGN_MERGE_ERROR_CONFLICT_KEY,
  CAMPAIGN_MERGE_ERROR_SERVER_KEY,
  CAMPAIGN_MERGE_ERROR_UNKNOWN_KEY,
  CAMPAIGN_STATUSES,
  ICampaign,
  ICampaignCreateParams,
  ICampaignDTO,
  ICampaignFilters,
  ICampaignRepository,
  ICampaignType,
  ICampaignUpdateParams,
  IForecastResult,
  IForecastResultByCampaignId,
  IForecastResultGroup,
  IMapForecastResult,
  MAP_MAX_SCREEN_IDS
} from "./model"

interface IGetCampaignParams {
  campaignIds?: number[]
  comparators: string[]
  match: "exact" | "contains" | "starts_with" | "ends_with"
  nameSearch: string | undefined
  orderIds: number[] | undefined
  advertiserIds: number[] | undefined
  statusIds: CAMPAIGN_STATUSES[]
  created_at: string[] | undefined
  startDate: string[] | undefined
  endDate: string[] | undefined
}

export const campaignRPO: ICampaignRepository = {
  getCampaigns(pagination: IServerPagination, filters: ICampaignFilters, type?: "RTB" | "Deal"): Promise<ICampaign[]> {
    return new Promise<ICampaign[]>((resolve, reject) => {
      const sort: string[] = []
      const offset = pagination.page * pagination.pageSize
      const limit = pagination.pageSize
      if (pagination.orderBy) {
        sort.push(`${pagination.orderBy},${pagination.order || "asc"}`)
      }
      const params = getGetCampaignsParams(filters)
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignList(
          offset,
          limit,
          params.nameSearch, // search
          params.match, // match => 'exact' | 'contains' | 'starts_with' | 'ends_with'
          sort.length > 0 ? sort : undefined, // order by => string[] (fieldname,[asc|desc]+)
          params.comparators, // comparators
          params.campaignIds, // ids
          params.statusIds, // statusIds
          undefined, // inventoryTypeId => 'ctv' | 'display' | 'dooh' | 'video',
          type || "RTB", // type => 'Deal' | 'Default' | 'RTB'
          undefined, // targetingLevelId => 'ron' | 'roc' | 'ros'
          undefined, // priorityChainId => 'low' | 'network' | 'channel' | 'permanent' | 'targeted' | 'rush'
          params.orderIds, // order ids
          params.startDate, // startDate (string)
          params.endDate, // endDate (string)
          undefined, // lastEditedAt
          params.created_at, // created_at string[]
          undefined, // isMonetized
          undefined, // isPending
          undefined, // languageId
          undefined, // userId
          undefined, // accountManagerId
          params.advertiserIds, // advertiser,
          undefined, // views number
          undefined // totalSpend number
        )
          .then(response => resolve(getCampaignsFromRaw(response)))
          .catch((error: any) => {
            console.error("Server error while searching campaigns", error)
            reject("campaign.search.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while searching all campaigns", ex)
        reject("campaign.search.error.unexpected")
      }
    })
  },
  getCampaignsCount(filters: ICampaignFilters, type?: "RTB" | "Deal"): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const params = getGetCampaignsParams(filters)
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignCount(
          undefined,
          undefined, // limit don't set else it won't work !!!
          params.nameSearch, // search
          params.match, // match => 'exact' | 'contains' | 'starts_with' | 'ends_with'
          undefined, // order by => string[] (fieldname,[asc|desc]+)
          params.comparators, // comparators
          params.campaignIds,
          params.statusIds, // statusIds
          undefined, // inventoryTypeId => 'ctv' | 'display' | 'dooh' | 'video',
          type || "RTB", // type => 'Deal' | 'Default' | 'RTB'
          undefined, // targetingLevelId => 'ron' | 'roc' | 'ros'
          undefined, // priorityChainId => 'low' | 'network' | 'channel' | 'permanent' | 'targeted' | 'rush'
          params.orderIds, // order ids
          params.startDate, // startDate (string)
          params.endDate, // endDate (string)
          undefined, // lastEditedAt
          params.created_at, // created_at string[]
          undefined, // isMonetized
          undefined, // isPending
          undefined, // languageId
          undefined, // userId
          undefined, // accountManagerId
          params.advertiserIds, // advertiser,
          undefined, // views number
          undefined // totalSpend number
        )
          .then(response => resolve(response.count || 0))
          .catch((error: any) => {
            console.error("Server error while searching campaigns", error)
            reject("campaign.search.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while searching all campaigns", ex)
        reject("campaign.search.error.unexpected")
      }
    })
  },
  getCampaign(campaignId: number): Promise<ICampaign> {
    return new Promise<ICampaign>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaign(campaignId)
          .then(response => resolve(getCampaignFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while getting campaign", error)
            if (error.status === 404) {
              return reject(NOT_FOUND_PATH)
            }
            reject("campaign.get.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting campaign", ex)
        reject("campaign.get.error.unexpected")
      }
    })
  },
  getCampaignStatuses(): Promise<CAMPAIGN_STATUSES[]> {
    return new Promise<CAMPAIGN_STATUSES[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignStatusList()
          .then(response => resolve(response.map(status => status.id as CAMPAIGN_STATUSES)))
          .catch((error: any) => {
            console.error("Server error while getting all campaign statuses", error)
            reject("campaign.get.statuses.all.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting all campaign statuses", ex)
        reject("campaign.get.statuses.all.error.unexpected")
      }
    })
  },
  getCampaignTypes(): Promise<ICampaignType[]> {
    return new Promise<ICampaignType[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignTypeList()
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting all campaign types", error)
            reject("campaign.get.type.all.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting all campaign types", ex)
        reject("campaign.get.type.all.error.unexpected")
      }
    })
  },
  create(params: ICampaignCreateParams): Promise<ICampaign> {
    return new Promise<ICampaign>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        setAudienceDefaultParams(params)
        CampaignAPI.postApiCampaign(params)
          .then(response => resolve(getCampaignFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while creating new campaign", error)
            reject("campaign.create.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while creating new campaign", ex)
        reject("campaign.create.error.unexpected")
      }
    })
  },
  update(params: ICampaignUpdateParams): Promise<ICampaign> {
    return new Promise<ICampaign>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        setAudienceDefaultParams(params)
        CampaignAPI.putApiCampaign(params)
          .then(response => resolve(getCampaignFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while updating campaign", error)
            reject("campaign.update.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while updating campaign", ex)
        reject("campaign.update.error.unexpected")
      }
    })
  },
  getCampaignGlobalForecast(campaignId: number, cad_exchange_rate: number): Promise<IForecastResult> {
    return new Promise<IForecastResult>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        const result: IForecastResult = {
          total_impressions: 0,
          total_screens: 0,
          total_spend: 0,
          cpmForecastTotalSpend: 0,
          cpmForecastTotalImpressions: 0,
          cpmForecastTotalScreens: 0,
          cpmForecastGroups: []
        }
        let processedPromiseCount = 0
        const onFinish = (hasError: boolean) => {
          if (hasError) {
            reject("campaign.get.forecast.error.server")
            return
          }
          if (++processedPromiseCount === 2) {
            resolve(result)
          }
        }
        ForecastAPI.getApiForecastDooh(campaignId)
          .then(response => {
            result.total_impressions = Math.trunc(response.total_impressions)
            result.total_screens = Math.trunc(response.total_screens)
            result.total_spend = Math.trunc(response.total_spend)
            onFinish(false)
          })
          .catch((error: any) => {
            console.error("Server error while getting campaign forecast", error)
            onFinish(true)
          })
        let steps = "0,4,8,12"
        if (cad_exchange_rate !== 1) {
          steps = `0,${4 * cad_exchange_rate},${8 * cad_exchange_rate},${12 * cad_exchange_rate}`
        }
        ForecastAPI.getApiForecastDoohCpm(campaignId, steps)
          .then(response => {
            const groups: IForecastResultGroup[] = []
            const responseGroup = response.groups
            for (let i = 0; i < 4; i++) {
              let group: IForecastResultGroup | undefined = Array.isArray(responseGroup)
                ? responseGroup.find(g => g.cpm_distribution_index === i)
                : undefined
              if (!group) {
                group = {
                  impressions: 0,
                  screen_count: 0,
                  worth_cad: 0
                }
              }
              groups.push(group)
            }
            result.cpmForecastGroups = groups
            result.cpmForecastTotalImpressions = Math.trunc(response.total_impressions)
            result.cpmForecastTotalScreens = Math.trunc(response.total_screens)
            result.cpmForecastTotalSpend = Math.trunc(response.total_spend)
            onFinish(false)
          })
          .catch((error: any) => {
            console.error("Server error while getting campaign CPM forecast", error)
            onFinish(true)
          })
      } catch (ex) {
        console.error("Unexpected error while getting campaign forecast", ex)
        reject("campaign.get.forecast.error.unexpected")
      }
    })
  },
  getCampaignsGlobalForecast(campaignIds: number[]): Promise<IForecastResultByCampaignId> {
    return new Promise<IForecastResultByCampaignId>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        const result: IForecastResultByCampaignId = {}
        const toProcessCount = campaignIds.length
        let processedPromiseCount = 0
        const onFinish = (hasError: boolean) => {
          if (hasError) {
            reject("campaign.get.forecasts.error.server")
            return
          }
          if (++processedPromiseCount === toProcessCount) {
            resolve(result)
          }
        }
        for (const campaignId of campaignIds) {
          ForecastAPI.getApiForecastDooh(campaignId)
            .then(response => {
              result[campaignId] = {
                total_impressions: Math.trunc(response.total_impressions),
                total_screens: Math.trunc(response.total_screens),
                total_spend: Math.trunc(response.total_spend)
              }
              onFinish(false)
            })
            .catch((error: any) => {
              console.error("Server error while getting simple campaign forecast", error)
              onFinish(true)
            })
        }
      } catch (ex) {
        console.error("Unexpected error while getting simple campaign forecast", ex)
        reject("campaign.get.forecasts.error.unexpected")
      }
    })
  },
  getCampaignMapForecastScreens(
    campaignId: number,
    boundaryBox: IBoundaryBox,
    resolutionInMeters: number,
    screenStats?: boolean
  ): Promise<IMapForecastResult> {
    return new Promise<IMapForecastResult>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        ForecastAPI.getApiForecastDoohMap(
          MAP_MAX_SCREEN_IDS,
          screenStats, // if true return details for each screen
          Number(resolutionInMeters.toFixed()),
          true,
          boundaryBox.sw.x,
          boundaryBox.sw.y,
          boundaryBox.ne.x,
          boundaryBox.ne.y,
          campaignId
        )
          .then(response => resolve(response as IMapForecastResult))
          .catch((error: any) => {
            console.error("Server error while getting campaign map forecast", error)
            reject("campaign.get.map.forecast.screens.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting campaign map forecast", ex)
        reject("campaign.get.map.forecast.screens.error.unexpected")
      }
    })
  },
  duplicate(campaignId: number): Promise<ICampaign> {
    return new Promise<ICampaign>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignDuplicate(campaignId, { status_id: CAMPAIGN_STATUSES.TEMPORARY as any })
          .then(response => resolve(getCampaignFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while duplicating campaign", error)
            if (error.status === 404) {
              return reject(NOT_FOUND_PATH)
            }
            reject("campaign.duplicate.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while duplicating campaign", ex)
        reject("campaign.duplicate.error.unexpected")
      }
    })
  },
  merge(campaignId1: number, campaignId2: number, campaign2LastEditeddAt?: string): Promise<ICampaign> {
    return new Promise<ICampaign>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        type params = Parameters<typeof CampaignAPI.putApiCampaignMerge>
        const body: params[2] = {}
        if (campaign2LastEditeddAt) {
          body.last_edited_at = campaign2LastEditeddAt
        }
        CampaignAPI.putApiCampaignMerge(campaignId1, campaignId2, body)
          .then(response => resolve(getCampaignFromDTO(response)))
          .catch((error: any) => {
            if (error.status === 409 && error.body && error.body.message === "updated_at changed since last checked") {
              console.error("Conflict error while merging campaigns", error)
              return reject(CAMPAIGN_MERGE_ERROR_CONFLICT_KEY)
            }
            console.error("Server error while merging campaigns", error)
            reject(CAMPAIGN_MERGE_ERROR_SERVER_KEY)
          })
      } catch (ex) {
        console.error("Unexpected error while merging campaigns", ex)
        reject(CAMPAIGN_MERGE_ERROR_UNKNOWN_KEY)
      }
    })
  },
  launchCampaign(campaignId: number): Promise<ICampaign> {
    return new Promise<ICampaign>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.putApiCampaignReview(campaignId)
          .then(response => resolve(getCampaignFromDTO(response)))
          .catch((error: any) => {
            console.error("Server error while launching campaign", error)
            if (error.status === 404) {
              return reject(NOT_FOUND_PATH)
            }
            reject("campaign.launch.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while launching campaign", ex)
        reject("campaign.launching.error.unexpected")
      }
    })
  }
}

function getCampaignsFromRaw(campaignDTOs: ICampaignDTO[]): ICampaign[] {
  return campaignDTOs.map(c => getCampaignFromDTO(c))
}

function getCampaignFromDTO(campaignDTO: ICampaignDTO): ICampaign {
  return {
    ...campaignDTO,
    status_id: campaignDTO.status_id as CAMPAIGN_STATUSES,
    start_date: getRepositoryDate(campaignDTO.start_date),
    end_date: getRepositoryDate(campaignDTO.end_date),
    created_at: getRepositoryDate(campaignDTO.created_at),
    confirmed_at: getRepositoryDate(campaignDTO.confirmed_at),
    billed_at: getRepositoryDate(campaignDTO.billed_at),
    reports: null,
    estimate: null,
    dspDeal: null
  }
}

function getGetCampaignsParams(filters: ICampaignFilters): IGetCampaignParams {
  let statusIds = filters.statuses
  if (statusIds.length === 0) {
    if (filters.isForecast) {
      statusIds = ALL_FORECAST_STATUSES.slice()
    } else {
      statusIds = ALL_CAMPAIGN_STATUSES.slice()
    }
  }
  const params: IGetCampaignParams = {
    comparators: [],
    match: "contains",
    campaignIds: filters.ids,
    nameSearch: filters.name || undefined,
    orderIds: filters.orderIds.length > 0 ? filters.orderIds : undefined,
    advertiserIds: filters.advertiserIds.length > 0 ? filters.advertiserIds : undefined,
    statusIds,
    created_at: filters.creationDate.date ? [filters.creationDate.date.toISOString()] : undefined,
    startDate: filters.startEndPeriod.endDate ? [filters.startEndPeriod.endDate.toISOString()] : undefined,
    endDate: filters.startEndPeriod.startDate ? [filters.startEndPeriod.startDate.toISOString()] : undefined
  }
  if (params.created_at) {
    params.comparators.push("created_at,gte")
  }
  if (params.startDate && params.endDate) {
    params.comparators.push("start_date,lte")
    params.comparators.push("end_date,gte")
  }
  return params
}
